import dayjs from "dayjs";
import {
  createRequestLog,
  deleteRequestLog,
  updateRequestLog,
} from "../../graphql/mutations";
import {
  requestLogsByChat,
  requestLogsByMessage,
  requestLogsByWorkspace,
  requestsByMonthAndWorkspace,
} from "../../graphql/queries";
import { makeGraphQLRequest } from "..";

const SUPPORT_LOOKBACK_MONTHS = 24;

const getRequestLogsByChat = async (chatID, nextToken = null) => {
  return makeGraphQLRequest({
    query: requestLogsByChat,
    variables: {
      chatID,
      sortDirection: "ASC",
      limit: 1000,
      nextToken,
    },
  });
};

const getRequestLogsByWorkspaceAndType = async (
  workspaceID,
  type = "support",
  nextToken = null
) => {
  return makeGraphQLRequest({
    query: requestLogsByWorkspace,
    variables: {
      workspaceID,
      filter: { type: { eq: type } },
      limit: 1000,
      nextToken,
    },
  });
};

// Support logs are written with srtKey = `${workspaceID}:YYYY-MM`, so the
// requestsByMonthAndWorkspace index (which has srtKey as the partition key
// and supports sortDirection + filter) is the efficient way to fetch them.
// Querying requestLogsByWorkspace with a type filter does a linear scan over
// every request log in the workspace, burying support entries behind
// thousands of regular AI requests. We walk the last N months instead.
const getAllRequestLogsByWorkspaceAndType = async (
  workspaceID,
  type = "support"
) => {
  const months = [];
  const base = dayjs();
  for (let i = 0; i < SUPPORT_LOOKBACK_MONTHS; i += 1) {
    months.push(base.subtract(i, "month").format("YYYY-MM"));
  }
  const results = await Promise.all(
    months.map((month) =>
      makeGraphQLRequest({
        query: requestsByMonthAndWorkspace,
        variables: {
          srtKey: `${workspaceID}:${month}`,
          filter: { type: { eq: type } },
          limit: 1000,
        },
      }).catch((error) => {
        console.warn(
          `[RequestLogs] failed to fetch ${type} logs for ${month}`,
          error
        );
        return null;
      })
    )
  );
  const allItems = [];
  results.forEach((result) => {
    const items = result?.data?.requestsByMonthAndWorkspace?.items || [];
    allItems.push(...items);
  });
  return allItems.filter((u) => u?._deleted !== true);
};

// Full-fidelity fetch for analytics: walks each month partition of
// requestsByMonthAndWorkspace WITH pagination and no type filter, returning
// complete RequestLog records (userID, modelType, totalCostUSD, ...) used for
// per-user/per-model consumption breakdowns. Mirrors the billing lambda's own
// read path (billingLogicCore.fetchRequestsForMonthKey): AI request logs are
// written with srtKey = `${workspaceID}-MM-yy` (DASH + MM-yy — e.g.
// `ws-123-09-26` — unlike the colon + YYYY-MM format the frontend uses for
// support logs, which live in their own partition). Querying any other key
// format here returns zero AI requests.
const getRequestLogsByWorkspaceAndMonths = async (workspaceID, monthKeys) => {
  const allItems = [];
  for (const month of monthKeys) {
    let nextToken = null;
    do {
      const result = await makeGraphQLRequest({
        query: requestsByMonthAndWorkspace,
        variables: {
          srtKey: `${workspaceID}-${month}`,
          limit: 1000,
          nextToken,
        },
      }).catch((error) => {
        console.warn(
          `[RequestLogs] failed to fetch logs for ${month}`,
          error
        );
        return null;
      });
      const connection = result?.data?.requestsByMonthAndWorkspace;
      allItems.push(...(connection?.items || []));
      nextToken = connection?.nextToken;
    } while (nextToken);
  }
  return allItems.filter((u) => u?._deleted !== true);
};

const createSupportLog = async ({
  workspaceID,
  supportHours,
  description,
  userID,
  createdBy,
  creationTime = Math.floor(dayjs().valueOf() / 1000),
}) => {
  return makeGraphQLRequest({
    query: createRequestLog,
    variables: {
      input: {
        srtKey: `${workspaceID}:${dayjs.unix(creationTime).format("YYYY-MM")}`,
        type: "support",
        status: "success",
        description,
        supportHours,
        workspaceID,
        createdBy,
        isFromToothFairy: true,
        userID,
        creationTime,
      },
    },
  });
};

const updateSupportLog = async ({
  id,
  _version,
  supportHours,
  description,
  creationTime,
}) => {
  return makeGraphQLRequest({
    query: updateRequestLog,
    variables: {
      input: { id, _version, supportHours, description, creationTime },
    },
  });
};

const deleteSupportLog = async ({ id, _version }) => {
  return makeGraphQLRequest({
    query: deleteRequestLog,
    variables: {
      input: { id, _version },
    },
  });
};

const getAllRequestLogsByChat = async (chatID) => {
  let allItems = [];
  let nextToken = null;
  do {
    const result = await getRequestLogsByChat(chatID, nextToken);
    const connection = result?.data?.requestLogsByChat;
    allItems = [...allItems, ...(connection?.items || [])];
    nextToken = connection?.nextToken;
  } while (nextToken);
  return allItems;
};

const getRequestLogsByMessage = async (messageID, nextToken = null) => {
  return makeGraphQLRequest({
    query: requestLogsByMessage,
    variables: {
      messageID,
      sortDirection: "ASC",
      limit: 1000,
      nextToken,
    },
  });
};

const getAllRequestLogsByMessage = async (messageID) => {
  let allItems = [];
  let nextToken = null;
  do {
    const result = await getRequestLogsByMessage(messageID, nextToken);
    const connection = result?.data?.requestLogsByMessage;
    allItems = [...allItems, ...(connection?.items || [])];
    nextToken = connection?.nextToken;
  } while (nextToken);
  return allItems;
};

export default {
  getRequestLogsByChat,
  getAllRequestLogsByChat,
  getRequestLogsByMessage,
  getAllRequestLogsByMessage,
  getRequestLogsByWorkspaceAndType,
  getAllRequestLogsByWorkspaceAndType,
  getRequestLogsByWorkspaceAndMonths,
  createSupportLog,
  updateSupportLog,
  deleteSupportLog,
};
